import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MagnifyingGlassIcon, PaperAirplaneIcon, ExclamationCircleIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import { useAuthStore } from '../store/authStore';
import { useTranslation } from 'react-i18next';
import apiClient from '../services/api';
import { LoadingOverlay } from '../components/ui/LoadingOverlay';

// Sample queries for "Try asking about" suggestions
const SAMPLE_QUERIES = [
  'Explore Paris',
  'Plan a trip to Italy',
  "What's the best time to visit Japan?",
  'Convert USD to EUR',
  'Build a 7-day Japan itinerary',
];

export const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return t('common:greeting.morning', 'Good morning');
    if (hour < 18) return t('common:greeting.afternoon', 'Good afternoon');
    return t('common:greeting.evening', 'Good evening');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!query.trim()) {
      return;
    }

    setLoading(true);
    setErrorMessage(null);

    try {
      // Call the classify endpoint
      const response = await apiClient.post('/classify/query', {
        query: query.trim()
      });

      const activity = response.data?.activity;

      // Map activity to route
      const activityRoutes: Record<string, string> = {
        'Destination Explorer': '/destinations',
        'Itinerary Builder': '/itinerary',
        'Travel Glossary': '/travel-glossary',
        'Travel Questions': '/ask-questions',
        'Ask Travel Questions': '/ask-questions',
        'Calculator Suite': '/calculators',
        'Currency Converter': '/calculators',
        'Trip Budget': '/calculators',
        'Tip Calculator': '/calculators',
        'Time Zone Calculator': '/calculators',
        'Distance Calculator': '/calculators',
        'Flight & Route Explorer': '/flights',
        'Accommodation Guide': '/accommodations',
        'Ground Transportation': '/transport',
        'Cultural Intelligence': '/cultural',
      };

      const route = activityRoutes[activity];

      if (route) {
        setRetryCount(0);
        navigate(route, { state: { initialQuery: query.trim() } });
      } else {
        if (retryCount === 0) {
          setErrorMessage('I couldn\'t determine the best tool for your query. Please try being more specific about what you need help with.');
          setRetryCount(1);
        } else {
          setErrorMessage('I\'m still unable to find a matching tool. Please browse the menu in the sidebar to select the tool that best fits your needs.');
          setRetryCount(0);
        }
      }
    } catch (error) {
      console.error('Error classifying query:', error);
      setErrorMessage('An error occurred while processing your request. Please try again or select a tool from the sidebar.');
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setQuery(suggestion);
    setErrorMessage(null);
    setRetryCount(0);
  };

  return (
    <div className="flex flex-col items-center justify-center h-full p-4 md:p-8">
      {/* Logo */}
      <div className="mb-4 md:mb-8">
        <GlobeAltIcon className="h-16 w-16 md:h-24 md:w-24 text-cyan-500" />
      </div>

      {/* Greeting */}
      <h1 className="text-2xl md:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-2 text-center">
        {getGreeting()}, {user?.name || user?.email?.split('@')[0] || 'there'}
      </h1>
      <p className="text-base md:text-xl text-gray-600 dark:text-gray-400 mb-8 md:mb-12 text-center">
        {t('common:dashboard.helpPrompt', 'Where would you like to go today?')}
      </p>

      {/* Search Bar */}
      <form onSubmit={handleSubmit} className="w-full max-w-3xl">
        <div className="relative flex items-center">
          <div className="absolute left-4">
            <MagnifyingGlassIcon className="h-6 w-6 text-gray-400" />
          </div>
          <input
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              if (errorMessage) setErrorMessage(null);
            }}
            placeholder={t('common:dashboard.searchPlaceholder', 'Ask a travel question or describe what you need...')}
            className="w-full pl-12 pr-12 py-4 text-lg border-2 border-gray-300 dark:border-gray-600 rounded-full focus:outline-none focus:border-cyan-500 dark:focus:border-cyan-400 transition-colors bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          />
          <div className="absolute right-4 flex items-center space-x-2">
            <button
              type="submit"
              disabled={loading || !query.trim()}
              className="p-2 text-cyan-500 hover:text-cyan-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <PaperAirplaneIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Error Message */}
        {errorMessage && (
          <div className="mt-4 flex items-start gap-3 p-4 bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800 rounded-lg">
            <ExclamationCircleIcon className="h-5 w-5 text-cyan-600 dark:text-cyan-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-cyan-800 dark:text-cyan-200">{errorMessage}</p>
          </div>
        )}
      </form>

      {/* Try asking about suggestions */}
      <div className="mt-6 md:mt-8 text-center w-full max-w-3xl">
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
          {t('common:dashboard.tryAsking', 'Try asking about:')}
        </p>
        <div className="flex flex-wrap justify-center gap-2">
          {SAMPLE_QUERIES.map((suggestion) => (
            <button
              key={suggestion}
              onClick={() => handleSuggestionClick(suggestion)}
              className="px-4 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full hover:bg-cyan-100 dark:hover:bg-cyan-900/30 hover:text-cyan-700 dark:hover:text-cyan-300 transition-colors"
            >
              {suggestion}
            </button>
          ))}
        </div>
      </div>

      <LoadingOverlay isVisible={loading} message="Finding the right tool..." subMessage="Analyzing your request" />
    </div>
  );
};

export default Dashboard;
